import React, { useState } from "react";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, MapPin, Home, Check, X, UserPlus, Users } from "lucide-react";

const invert = { parent: "child", child: "parent", spouse: "spouse", sibling: "sibling" };
const relLabel = { parent: "parent", child: "child", spouse: "spouse / partner", sibling: "sibling" };

export function AnalyzeNotesPanel({ member, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [done, setDone] = useState({});
  const [choices, setChoices] = useState({}); // index -> candidate id | 'new'

  const run = async () => {
    setLoading(true);
    setDone({});
    try {
      const data = await api.analyzeNotes({ text: member.bio, member_id: member.id });
      setResult(data);
      const init = {};
      (data.relatives || []).forEach((r, i) => {
        init[i] = r.match ? r.match.id : r.candidates?.length === 1 ? r.candidates[0].id : "new";
      });
      setChoices(init);
      if (!data.origin && !data.current && (!data.relatives || data.relatives.length === 0)) {
        toast.info("Nothing to suggest from these notes");
      }
    } catch {
      toast.error("Could not analyze notes");
    } finally {
      setLoading(false);
    }
  };

  const mark = (key) => setDone((d) => ({ ...d, [key]: true }));

  const acceptOrigin = async () => {
    try { await api.update(member.id, { origin_place: result.origin.place }); toast.success("Origin saved"); mark("origin"); onChanged?.(); }
    catch { toast.error("Could not save location"); }
  };
  const acceptCurrent = async () => {
    try { await api.update(member.id, { current_place: result.current.place }); toast.success("Current location saved"); mark("current"); onChanged?.(); }
    catch { toast.error("Could not save location"); }
  };
  const acceptRelative = async (rel, i) => {
    const choice = choices[i];
    try {
      if (choice && choice !== "new") {
        await api.link(member.id, { target_id: choice, relation: invert[rel.relation] });
        toast.success("Relative linked");
      } else {
        await api.create({ full_name: rel.name, relate_to: member.id, relation: rel.relation });
        toast.success(`Added ${rel.name}`);
      }
      mark(`rel-${i}`);
      onChanged?.();
    } catch { toast.error("Could not link relative"); }
  };

  if (!member.bio?.trim()) return null;

  const hasSuggestions = result && ((result.origin && !done.origin) || (result.current && !done.current) || (result.relatives || []).some((_, i) => !done[`rel-${i}`]));

  return (
    <div className="mt-4">
      <Button onClick={run} disabled={loading} variant="outline" data-testid="analyze-notes-button" className="w-full border-[#D4A017]/40 bg-[#D4A017]/5 text-[#D4A017] hover:bg-[#D4A017]/10">
        <Sparkles size={16} className="mr-2" />
        {loading ? "Reading the notes…" : "Analyze notes with AI"}
      </Button>

      {result && hasSuggestions && (
        <div className="mt-3 space-y-2 rounded-2xl border border-[#D8E0E8] bg-white p-3" data-testid="analyze-suggestions">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">Suggestions — tap to confirm</div>

          {result.origin && !done.origin && (
            <Row testid="suggestion-origin" icon={<MapPin size={16} className="text-[#D4A017]" />} onAccept={acceptOrigin} onDismiss={() => mark("origin")} acceptTestid="accept-origin-button">
              <span className="text-[#64748B]">From: </span>{result.origin.place}
            </Row>
          )}
          {result.current && !done.current && (
            <Row testid="suggestion-current" icon={<Home size={16} className="text-[#082D4A]" />} onAccept={acceptCurrent} onDismiss={() => mark("current")} acceptTestid="accept-current-button">
              <span className="text-[#64748B]">Lives in: </span>{result.current.place}
            </Row>
          )}

          {(result.relatives || []).map((rel, i) => {
            if (done[`rel-${i}`]) return null;
            const ambiguous = rel.candidates && rel.candidates.length > 1;
            return (
              <div key={i} data-testid={`suggestion-relative-${i}`} className="rounded-xl bg-[#F4F6F8] p-2 text-sm">
                <div className="flex items-center gap-2">
                  {choices[i] !== "new" ? <Users size={16} className="text-[#082D4A]" /> : <UserPlus size={16} className="text-[#D4A017]" />}
                  <span className="flex-1 leading-tight text-[#082D4A]">
                    <span className="font-medium">{rel.name}</span> <span className="text-[#64748B]">as {relLabel[rel.relation]}</span>
                  </span>
                  <button onClick={() => acceptRelative(rel, i)} data-testid={`accept-relative-${i}-button`} className="flex h-7 w-7 items-center justify-center rounded-full bg-[#082D4A] text-white hover:bg-[#08223A]"><Check size={15} /></button>
                  <button onClick={() => mark(`rel-${i}`)} className="flex h-7 w-7 items-center justify-center rounded-full border border-[#D8E0E8] bg-white text-[#64748B] hover:bg-[#E8EEF3]"><X size={15} /></button>
                </div>
                {ambiguous && (
                  <div className="mt-2 pl-6">
                    <div className="mb-1 text-xs text-[#D4A017]">More than one match — pick one:</div>
                    <Select value={choices[i]} onValueChange={(v) => setChoices((c) => ({ ...c, [i]: v }))}>
                      <SelectTrigger data-testid={`relative-${i}-chooser`} className="h-8 bg-white text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {rel.candidates.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.full_name}{c.birth_date ? ` (${c.birth_date})` : ""}{c.current_place ? ` — ${c.current_place}` : ""}
                          </SelectItem>
                        ))}
                        <SelectItem value="new">Add {rel.name} as a new person</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Row({ children, icon, onAccept, onDismiss, testid, acceptTestid }) {
  return (
    <div className="flex items-center gap-2 rounded-xl bg-[#F4F6F8] p-2 text-sm" data-testid={testid}>
      <span className="shrink-0">{icon}</span>
      <span className="flex-1 leading-tight text-[#082D4A]">{children}</span>
      <button onClick={onAccept} data-testid={acceptTestid} className="flex h-7 w-7 items-center justify-center rounded-full bg-[#082D4A] text-white hover:bg-[#08223A]"><Check size={15} /></button>
      <button onClick={onDismiss} className="flex h-7 w-7 items-center justify-center rounded-full border border-[#D8E0E8] bg-white text-[#64748B] hover:bg-[#E8EEF3]"><X size={15} /></button>
    </div>
  );
}
